import { useLocalization } from "@progress/kendo-react-intl";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSignal } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { ClientRoutes, ReactQueryKeys } from "../../../../Constants/ClientRoutingConstants";
import useAssetStore from "../../../../Zustand/hmeDashboardStore";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import { SvgIcon } from "@progress/kendo-react-common";
import { arrowRotateCwIcon, circleIcon, clipboardIcon, bellIcon } from '@progress/kendo-svg-icons';
import AssetOverviewTab, { AssetOverviewTabRef } from "./AssetOverviewTab";
import AssetAnalysisTab, { AssetAnalysisTabRef } from "./AssetAnalysisTab";
import AssetInsightsTab, { AssetInsightsTabRef } from "./AssetInsightsTab";
import AssetNotesTab, { AssetNotesTabRef } from "./AssetNotesTab";
import { useQuery, useQueryClient } from "react-query";
import assetsDashboardApiService from "../Utilities/AssetsDashboardApiService";
import ErrorCard from "../../../Shared/Components/Cards/ErrorCard";
import { Breadcrumb, DataModel } from "@progress/kendo-react-layout";
import SelectedAssetMetricsReadDto from "../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import { enMessages } from "../../../../messages/en-US";
import moment from 'moment';
import AssetMetricsReadDto from "../../../../Models/Dashboard/AssetMetricsReadDto";
import useIdentityStore from "../../../../Zustand/identityStore";
import { useResetAssetsDashboardFiltersOnLeave } from "../hooks/useResetAssetsDashboardFiltersOnLeave";
import ClearCacheButton from "../../../Shared/Components/ClearCacheButton";

interface Props {
  selectedAsset?: any;
}

export default function AssetSelectedPage({ selectedAsset }: Props) {
  useResetAssetsDashboardFiltersOnLeave();
  const localizationService = useLocalization();
  const [selectedButton, setSelectedButton] = useState(0);
  const currentUserRole = useIdentityStore((x) => x.user?.profile?.role);
  const isSuperAdmin = currentUserRole === 'SuperAdmin';
  const analysisTabRef = useRef<AssetAnalysisTabRef>(null);
  const overviewTabRef = useRef<AssetOverviewTabRef>(null);
  const insightsTabRef = useRef<AssetInsightsTabRef>(null);
  const notesTabRef = useRef<AssetNotesTabRef>(null);

  // Shared date filter that persists across tab switches
  const [sharedDateFilter, setSharedDateFilter] = useState({
    endDate: moment().endOf('day').toDate(),
    startDate: moment().subtract(7, 'days').startOf('day').toDate(),
  });

  // Get the asset id from the url and fetch the asset metrics using the id because the selectedAsset is not updated immediately
  const navigate = useNavigate();
  const { id } = useParams();

  const setSelectedAsset = useAssetStore(state => state.setSelectedAsset);
  const queryClient = useQueryClient();
  
  // Get machineryType from react-query cache
  const getMachineryTypeFromCache = (assetId: string): string | undefined => {
    const cachedAssets = queryClient.getQueryData<AssetMetricsReadDto[]>([ReactQueryKeys.AllAssetsMetricsQuery]);
    return cachedAssets?.find(a => a.id === assetId)?.machineryType;
  };

  // Fetch all assets metrics if cache is empty (needed for machineryType after reload)
  // Always fetch if we don't have machineryType yet
  const needsMachineryType = !!id && !getMachineryTypeFromCache(id || '') && !(selectedAsset as any)?.machineryType;
  const allAssetsMetricsQuery = useQuery<AssetMetricsReadDto[]>(
    [ReactQueryKeys.AllAssetsMetricsQuery],
    () => assetsDashboardApiService.getAllAssetsMetrics(),
    {
      enabled: needsMachineryType,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  const selectedAssetQuery = useQuery<SelectedAssetMetricsReadDto>(
    [ReactQueryKeys.AssetMetricsQuery, id],
    () => assetsDashboardApiService.getAssetMetrics(id!),
    {
      enabled: !!id,
      onSuccess: (data) => {
        // Preserve machineryType from cache (comes from assetsMetricsQuery.data or allAssetsMetricsQuery)
        const machineryType = (selectedAsset as any)?.machineryType || 
                              (id ? getMachineryTypeFromCache(id) : undefined) ||
                              (allAssetsMetricsQuery.data?.find(a => a.id === id)?.machineryType);
        const mergedData = {
          ...data,
          ...selectedAsset,
          ...(machineryType && { machineryType })
        };
        setSelectedAsset(mergedData);
      },
    }
  );

  // Preserve machineryType from cache or allAssetsMetricsQuery if not in selectedAsset
  const machineryType = (selectedAsset as any)?.machineryType || 
                        (id ? getMachineryTypeFromCache(id) : undefined) ||
                        (allAssetsMetricsQuery.data?.find(a => a.id === id)?.machineryType);
  const asset = selectedAsset 
    ? { ...selectedAsset, ...(machineryType && { machineryType }) }
    : selectedAssetQuery.data 
      ? { ...selectedAssetQuery.data, ...(machineryType && { machineryType }) }
      : null;

  // Wait for machineryType if we need it
  const isLoadingMachineryType = needsMachineryType && allAssetsMetricsQuery.isLoading;
  
  // Ensure we have machineryType before rendering (especially important for Locomotive to show Fuel Burn tile)
  const hasRequiredData = asset && (!needsMachineryType || machineryType !== undefined);

  const handleBack = () => {
    setSelectedAsset(null);
    navigate(ClientRoutes.AssetsDashboardPage);
  };

  const handleRefresh = async () => {
    // Refetch the main asset data
    await selectedAssetQuery.refetch();
    
    // Refetch all assets metrics if needed
    if (needsMachineryType) {
      await allAssetsMetricsQuery.refetch();
    }

    // Then refresh the active tab if available
    if (selectedButton === 0 && overviewTabRef.current) {
      // If we're on the overview tab, refresh only the overview data (including alerts)
      await overviewTabRef.current.refresh();
    } else if (selectedButton === 1 && analysisTabRef.current) {
      // If we're on the analysis tab, refresh the analysis data
      await analysisTabRef.current.refresh();
    } else if (selectedButton === 2 && notesTabRef.current) {
      // If we're on the notes tab, refresh the notes data
      await notesTabRef.current.refresh();
    } else if (selectedButton === 3 && insightsTabRef.current) {
      // If we're on the insights tab, refresh the insights data
      await insightsTabRef.current.refresh();
    }
  };

  // Wipe this asset's compliance cache entries, then reload the asset data.
  const handleClearAssetCache = async () => {
    await assetsDashboardApiService.clearMachineComplianceCache(id!);
    await handleRefresh();
  };

  const items: DataModel[] = [
    {id: 'location', text: asset?.location},
    { id: 'asset', text: asset?.name }
  ];

  const [data, setData] = useState<DataModel[]>(items); 

  useEffect(() => {
    setData(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetQuery.data]);

  useEffect(() => {
    // If there is no id in the URL, clear the selected asset
    if (!id) {
      setSelectedAsset(null);
    }
  }, [id, setSelectedAsset]);

  useEffect(() => {
    if (!isSuperAdmin && selectedButton === 3) {
      setSelectedButton(0);
    }
  }, [isSuperAdmin, selectedButton]);

  if (selectedAssetQuery.isLoading || isLoadingMachineryType || !hasRequiredData) {
    return <LoadingSpinner />;
  } else if (selectedAssetQuery.isError) {
    return <ErrorCard />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-68px)] bg-gray-100">
      <div className="flex items-center justify-between px-2 py-2 flex-none bg-white">
        <div className="flex items-center ">
          <Button 
            onClick={handleBack}
            fillMode="clear"
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </Button>
          <div className="flex items-center gap-5 w-full">
            <div>
              <Breadcrumb data={data} disabled={true} className="breadcrumb-override text-lg" />
            </div>
            <div className="flex items-center gap-3 text-sm">
              <div className="flex items-center gap-4">
                <Tooltip 
                  anchorElement="target" 
                  position="bottom"
                  parentTitle={true}
                >
                  <div className="align-items-center" title={
                    `${localizationService.toLanguageString('custom.compliance', enMessages.custom.compliance)}: ${
                      asset?.complianceStatus === 'Compliant' ? localizationService.toLanguageString('custom.compliant', enMessages.custom.compliant) :
                      asset?.complianceStatus === 'NonCompliant' ? localizationService.toLanguageString('custom.nonCompliant', enMessages.custom.nonCompliant) :
                      asset?.complianceStatus === 'PartiallyCompliant' ? localizationService.toLanguageString('custom.partiallyCompliant', enMessages.custom.partiallyCompliant) :
                      'Unknown'}`
                  }>
                    <SvgIcon icon={clipboardIcon} size="large" className="text-gray-700"/>
                    <SvgIcon 
                      icon={circleIcon} 
                      style={{color: asset?.complianceStatus === 'Compliant' ? '#3FA43F' : asset?.complianceStatus === 'NonCompliant' ? '#FF3630' : asset?.complianceStatus === 'PartiallyCompliant' ? '#FFBF00' : '#85868E' }} 
                      size="large" 
                    />
                  </div>
                </Tooltip>
                <Tooltip 
                  anchorElement="target" 
                  position="bottom"
                  parentTitle={true}
                >
                  <div className="align-items-center" title={
                    `${localizationService.toLanguageString('custom.connectivity', enMessages.custom.connectivity)}: ${
                      asset?.connectivityStatus === 'Online' ? localizationService.toLanguageString('custom.online', enMessages.custom.online) :
                      asset?.connectivityStatus === 'Offline' ? localizationService.toLanguageString('custom.oneOrMoreDevicesAreOffline', enMessages.custom.oneOrMoreDevicesAreOffline) :
                      asset?.connectivityStatus === 'Partial' ? localizationService.toLanguageString('custom.partialConnected', enMessages.custom.partialConnected) :
                      'Unknown'
                    }`
                  }>
                    <FontAwesomeIcon icon={faSignal} size="lg" className="text-gray-700"/>
                    <SvgIcon 
                      icon={circleIcon} 
                      style={{color: asset?.connectivityStatus === 'Online' ? '#3FA43F' : asset?.connectivityStatus === 'Offline' ? '#FF3630' : asset?.connectivityStatus === 'Partial' ? '#FFBF00' : '#85868E' }} 
                      size="large" 
                    />
                  </div>
                </Tooltip>
                <Tooltip 
                  anchorElement="target" 
                  position="bottom"
                  parentTitle={true}
                >
                  <div className="align-items-center" title={
                    `${localizationService.toLanguageString('custom.alerts', enMessages.custom.alerts)}: ${asset?.alert?.value ?? 0}`
                  }>
                    <SvgIcon icon={bellIcon} size="large" className="text-gray-700"/>
                    <SvgIcon 
                      icon={circleIcon} 
                      style={{color: asset?.alert?.value > 0 ? '#FF3630' : '#3FA43F' }} 
                      size="large" 
                    />
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 pr-3">
          <ClearCacheButton
            onClear={handleClearAssetCache}
            confirmationMessage={localizationService.toLanguageString('custom.clearAssetCacheConfirmation', enMessages.custom.clearAssetCacheConfirmation)}
          />
          <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
            <Button
              svgIcon={arrowRotateCwIcon}
              onClick={handleRefresh}
              title={localizationService.toLanguageString('custom.refresh', enMessages.custom.refresh)}
            />
          </Tooltip>
        </div>
      </div>
      <div className="pl-3 bg-white">
        <ButtonGroup>
          <Button
            fillMode="solid"
            style={{
              borderRadius: "0px",
              backgroundColor: "transparent",
              borderColor: "transparent",
              borderBottom: selectedButton === 0 ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
              color: selectedButton === 0 ? "var(--kendo-color-primary)" : "inherit"
            }}
            onClick={() => setSelectedButton(0)}
          >
            {localizationService.toLanguageString('custom.overview', enMessages.custom.overview)}
          </Button>
          <Button
            fillMode="solid"
            style={{
              borderRadius: "0px",
              backgroundColor: "transparent",
              borderColor: "transparent",
              borderBottom: selectedButton === 1 ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
              color: selectedButton === 1 ? "var(--kendo-color-primary)" : "inherit"
            }}
            onClick={() => setSelectedButton(1)}
          >
            {localizationService.toLanguageString('custom.analysis', enMessages.custom.analysis)}
          </Button>
          <Button
            fillMode="solid"
            style={{
              borderRadius: "0px",
              backgroundColor: "transparent",
              borderColor: "transparent",
              borderBottom: selectedButton === 2 ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
              color: selectedButton === 2 ? "var(--kendo-color-primary)" : "inherit"
            }}
            onClick={() => setSelectedButton(2)}
          >
            {localizationService.toLanguageString('custom.notes', enMessages.custom.notes)}
          </Button>
          {isSuperAdmin && (
            <Button
              fillMode="solid"
              style={{
                borderRadius: "0px",
                backgroundColor: "transparent",
                borderColor: "transparent",
                borderBottom: selectedButton === 3 ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
                color: selectedButton === 3 ? "var(--kendo-color-primary)" : "inherit"
              }}
              onClick={() => setSelectedButton(3)}
            >
              {localizationService.toLanguageString('custom.insights', enMessages.custom.insights)}
            </Button>
          )}
        </ButtonGroup>
      </div>
      <div className="relative flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col">
          {selectedButton === 0 && (
            <AssetOverviewTab 
              ref={overviewTabRef}
              asset={asset!}
              dateFilter={sharedDateFilter}
              setDateFilter={setSharedDateFilter}
            />
          )}
          {selectedButton === 1 && (
            <AssetAnalysisTab 
              ref={analysisTabRef}
              asset={asset}
              dateFilter={sharedDateFilter}
              setDateFilter={setSharedDateFilter}
            />
          )}
          {selectedButton === 2 && (
            <AssetNotesTab
              ref={notesTabRef}
              asset={asset!}
            />
          )}
          {isSuperAdmin && selectedButton === 3 && (
            <AssetInsightsTab
              ref={insightsTabRef}
              asset={asset!}
              dateFilter={sharedDateFilter}
              setDateFilter={setSharedDateFilter}
            />
          )}
        </div>
        {selectedAssetQuery.isFetching && !selectedAssetQuery.isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>
  );
}